/**
 * 茨城県の背景ツアー「茨城めぐり」。10か所、それぞれに3つの豆知識。
 *
 * 書いてはいけないこと。
 * - ひたち海浜公園は、遊園地の観覧車を画角に入れない。
 * - 袋田の滝は、観瀑台・エレベーター・吊り橋といった現代の構築物を入れない。町のキャラクターも描かない。
 * - 竜神大吊橋は、バンジージャンプ事業者の看板やロゴを入れない。
 * - 霞ヶ浦は、動力の観光船が並走しないようにする。帆引き船だけを描く。
 * - 鹿島神宮は境内の入口と御手洗池口に鳥居がある。楼門と奥参道だけを描いて鳥居は外す。
 * - 大洗磯前神社の神磯の鳥居は、鳥居が主役で外せないうえに禁足地なので入れていない。
 * - 月待の滝は二十三夜尊の霊場なので入れていない。
 * - 筑波山は遠景にする。山頂付近のケーブルカーやロープウェイの駅舎を入れない。
 * - 牛久大仏は入れない。
 * - 大洗サンビーチは、町内のキャラクター装飾やマリンタワーが入るので入れていない。
 */

import type { StageTour } from '../types';

export const ibarakiSceneryTour: StageTour = {
  scenes: [
    {
      id: 'nemophila',
      name: 'みはらしの丘のネモフィラ',
      area: 'ひたちなか市',
      image: '/assets/scenery/ibaraki/scenes/01_nemophila.webp',
      hotspots: [
        {
          id: 'nemophila-count',
          x: 0.3,
          y: 0.5,
          title: '4.2ヘクタールに530万本',
          body: '約4.2ヘクタールの「みはらしの丘」一面に、およそ530万本ものネモフィラが咲き誇る。',
          source: 'https://www.pref.ibaraki.jp/soshiki/eigyo/promo/20260522.html',
          mapQuery: '国営ひたち海浜公園 みはらしの丘',
        },
        {
          id: 'nemophila-park',
          x: 0.6,
          y: 0.72,
          title: '総面積350ヘクタールの国営公園',
          body: '茨城県ひたちなか市に国が設置した国営公園で、総面積は350ヘクタール。現在のところ約68パーセントにあたる237.1ヘクタールが開園している。',
          source: 'https://www.ktr.mlit.go.jp/kaihin/kaihin00001.html',
          mapQuery: '国営ひたち海浜公園',
        },
        {
          id: 'nemophila-history',
          x: 0.8,
          y: 0.3,
          title: 'もとは射爆撃場だった',
          body: 'もともとは米軍の射爆撃場で、昭和48年3月15日に水戸対地射爆撃場跡が日本政府に返還された。昭和59年に起工式、平成3年10月5日に最初の区間が開園した。',
          source: 'https://www.ktr.mlit.go.jp/kaihin/kaihin00001.html',
          mapQuery: 'ひたちなか市 海浜公園',
        },
      ],
    },
    {
      id: 'ryujin-bridge',
      name: '竜神大吊橋',
      area: '常陸太田市',
      image: '/assets/scenery/ibaraki/scenes/02_ryujin.webp',
      hotspots: [
        {
          id: 'ryujin-bridge-size',
          x: 0.3,
          y: 0.4,
          title: '橋長375メートル、地上高100メートル',
          body: '橋長は375メートルで、歩行者用の吊り橋としては日本でも有数の長さを誇る。地上高は100メートル。',
          source: 'https://www.city.hitachiota.ibaraki.jp/miryoku/miru/facility/page000237.html',
          mapQuery: '竜神大吊橋',
        },
        {
          id: 'ryujin-bridge-blue',
          x: 0.6,
          y: 0.6,
          title: '青くなったのは2001年',
          body: '1994年に開通し、2001年に改修されたときに現在の青い色になった。橋の中央には透明なアクリル板があり、下のダム湖面が見える。',
          source: 'https://www.city.hitachiota.ibaraki.jp/miryoku/miru/facility/page000237.html',
          mapQuery: '常陸太田市 竜神大吊橋',
        },
        {
          id: 'ryujin-bridge-events',
          x: 0.78,
          y: 0.8,
          title: '鯉のぼりと灯籠と紅葉',
          body: '竜神峡は茨城百景のひとつ。年間を通じて鯉のぼり祭り、灯籠まつり、紅葉まつりが開かれる。周囲には八溝山系と阿武隈山系の山並みが見える。',
          source: 'https://www.city.hitachiota.ibaraki.jp/miryoku/miru/facility/page000237.html',
          mapQuery: '竜神峡',
        },
      ],
    },
    {
      id: 'kodokan',
      name: '弘道館',
      area: '水戸市',
      image: '/assets/scenery/ibaraki/scenes/03_kodokan.webp',
      hotspots: [
        {
          id: 'kodokan-founded',
          x: 0.28,
          y: 0.35,
          title: '天保12年に建てられた藩校',
          body: '天保12年（1841年）に水戸藩第9代藩主の徳川斉昭によって創設された藩校。',
          source: 'https://www.ibarakiguide.jp/spot.php?mode=detail&code=648',
          mapQuery: '弘道館',
        },
        {
          id: 'kodokan-scale',
          x: 0.58,
          y: 0.6,
          title: '藩校としては日本最大規模とされる',
          body: '茨城県観光物産協会は、藩校としては日本最大規模と紹介している。学問・武芸から医学・薬学・天文学まで幅広い分野の教育を行っていた。',
          source: 'https://www.ibarakiguide.jp/spot.php?mode=detail&code=648',
          mapQuery: '水戸市 弘道館',
        },
        {
          id: 'kodokan-gate',
          x: 0.5,
          y: 0.82,
          title: '藩主が来るときだけ開く門',
          body: '正門は重要文化財。本瓦葺きの四脚門で、藩主が来館する際など正式の場合にのみ開門した。',
          source: 'https://www.ibarakiguide.jp/spot.php?mode=detail&code=648',
          mapQuery: '弘道館 正門',
        },
      ],
    },
    {
      id: 'hobikisen',
      name: '霞ヶ浦の帆引き船',
      area: 'かすみがうら市',
      image: '/assets/scenery/ibaraki/scenes/04_hobikisen.webp',
      hotspots: [
        {
          id: 'hobikisen-invention',
          x: 0.28,
          y: 0.4,
          title: '1880年に考案された',
          body: '1880年に旧霞ヶ浦町の折本良平がシラウオ漁のために考案した。その後はワカサギ漁の主役となり、1967年までの約100年間、霞ヶ浦漁業の花形だった。',
          source: 'https://www.city.kasumigaura.lg.jp/page/page002216.html',
          mapQuery: '霞ヶ浦 帆引き船',
        },
        {
          id: 'hobikisen-kite',
          x: 0.58,
          y: 0.65,
          title: '凧の原理で横に流す',
          body: '凧の原理を応用し、船を横方向に流しながら漁をする。世界で唯一のこの種の漁船とされる。無風の日は漁をしなかった。',
          source: 'https://www.city.kasumigaura.lg.jp/page/page002216.html',
          mapQuery: 'かすみがうら市 帆引き船',
        },
        {
          id: 'hobikisen-lake',
          x: 0.8,
          y: 0.3,
          title: '国内第2位の湖',
          body: '霞ヶ浦は国内第2位の湖面積をもち、流域面積は茨城県全体の3分の1以上を占める。夏には観光帆曳船が運航され、霞ヶ浦の夏の風物詩になっている。',
          source: 'https://www.ibarakiguide.jp/spot.php?mode=detail&code=1202',
          mapQuery: '霞ヶ浦',
        },
      ],
    },
    {
      id: 'kashima-jingu',
      name: '鹿島神宮 奥参道',
      area: '鹿嶋市',
      image: '/assets/scenery/ibaraki/scenes/05_kashima.webp',
      hotspots: [
        {
          id: 'kashima-jingu-romon',
          x: 0.22,
          y: 0.35,
          title: '日本三大楼門のひとつとされる',
          body: '鹿島神宮は楼門を日本三大楼門のひとつと紹介している。高さ約13メートルで重要文化財に指定されている。寛永11年に水戸徳川家の初代藩主により奉納された。',
          source: 'https://kashimajingu.jp/guidance/境内案内/',
          mapQuery: '鹿島神宮 楼門',
        },
        {
          id: 'kashima-jingu-path',
          x: 0.55,
          y: 0.55,
          title: '奥宮へ300メートル',
          body: '奥宮に向かって300メートルほど伸びる奥参道は、鬱蒼とした巨木に覆われ、荘厳な雰囲気をたたえている。',
          source: 'https://kashimajingu.jp/guidance/境内案内/',
          mapQuery: '鹿島神宮 奥参道',
        },
        {
          id: 'kashima-jingu-forest',
          x: 0.8,
          y: 0.78,
          title: '600種を超える樹叢',
          body: '樹叢は茨城県指定の天然記念物で、その種類は600種以上にもおよび、生育の南限と北限の植物が混じっている。',
          source: 'https://kashimajingu.jp/guidance/境内案内/',
          mapQuery: '鹿島神宮',
        },
      ],
    },
    {
      id: 'hananuki',
      name: '花貫渓谷 汐見滝吊り橋',
      area: '高萩市',
      image: '/assets/scenery/ibaraki/scenes/06_hananuki.webp',
      hotspots: [
        {
          id: 'hananuki-length',
          x: 0.3,
          y: 0.55,
          title: '長さ約60メートル',
          body: '汐見滝吊り橋の長さは約60メートル。花貫ダムから名馬里ヶ淵、小滝沢キャンプ場にかけて美しい景観が続く。',
          source: 'https://www.city.takahagi.ibaraki.jp/kankou/spot/sizen/page000012.html',
          mapQuery: '汐見滝吊り橋',
        },
        {
          id: 'hananuki-tunnel',
          x: 0.55,
          y: 0.3,
          title: '紅葉のトンネル',
          body: '高萩市は「特に秋は、川沿いに生い茂る木々の枝が吊り橋の左右からせり出し、華やかな紅葉のトンネルとなります」と記している。',
          source: 'https://www.city.takahagi.ibaraki.jp/kankou/spot/sizen/page000012.html',
          mapQuery: '花貫渓谷',
        },
        {
          id: 'hananuki-season',
          x: 0.78,
          y: 0.78,
          title: '見頃は11月中旬から下旬',
          body: '例年の紅葉の見頃は11月中旬から下旬。吊り橋からは花貫川の清流と汐見滝も見える。',
          source: 'https://www.ibarakiguide.jp/spot.php?mode=detail&code=236',
          mapQuery: '高萩市 花貫渓谷',
        },
      ],
    },
    {
      id: 'hinuma',
      name: '涸沼',
      area: '茨城町',
      image: '/assets/scenery/ibaraki/scenes/07_hinuma.webp',
      hotspots: [
        {
          id: 'hinuma-ramsar',
          x: 0.28,
          y: 0.35,
          title: '2015年にラムサール条約湿地',
          body: '登録番号2232、登録は2015年5月28日、面積935ヘクタール。所在地は茨城県鉾田市・茨城町・大洗町。',
          source: 'https://www.env.go.jp/nature/ramsar/conv/ramsarleaflet/21_Hinuma.pdf',
          mapQuery: '涸沼',
        },
        {
          id: 'hinuma-brackish',
          x: 0.55,
          y: 0.7,
          title: '関東地方最大の汽水湖',
          body: '関東地方最大の汽水湖で、塩分の濃さが場所によって違う。ヤマトシジミは涸沼の特産物として、いまも涸沼の漁業を支えている。',
          source: 'https://www.env.go.jp/nature/ramsar/conv/ramsarleaflet/21_Hinuma.pdf',
          mapQuery: '涸沼 茨城町',
        },
        {
          id: 'hinuma-birds',
          x: 0.78,
          y: 0.5,
          title: 'スズガモが5000羽',
          body: '88種以上の鳥類が確認されている。スズガモは東南アジア地域個体群の個体数の1パーセントを超える5000羽程度が毎年飛来する。ヒヌマイトトンボも生息している。',
          source: 'https://www.env.go.jp/nature/ramsar/conv/ramsarleaflet/21_Hinuma.pdf',
          mapQuery: '涸沼自然公園',
        },
      ],
    },
    {
      id: 'fukuroda',
      name: '袋田の滝',
      area: '大子町',
      image: '/assets/scenery/ibaraki/scenes/08_fukuroda.webp',
      hotspots: [
        {
          id: 'fukuroda-size',
          x: 0.3,
          y: 0.35,
          title: '高さ120メートル、幅73メートル',
          body: '日本三名瀑に数えられ、高さ120メートル、幅73メートルの大きさを誇る。国名勝への指定は平成27年3月10日。',
          source: 'https://www.town.daigo.ibaraki.jp/page/page001474.html',
          mapQuery: '袋田の滝',
        },
        {
          id: 'fukuroda-yodo',
          x: 0.6,
          y: 0.6,
          title: '別名は四度の滝',
          body: '大岩壁を四段に落下することから、別名「四度（よど）の滝」とも呼ばれる。',
          source: 'https://www.ibarakiguide.jp/spot.php?mode=detail&code=5',
          mapQuery: '大子町 袋田の滝',
        },
        {
          id: 'fukuroda-saigyo',
          x: 0.78,
          y: 0.82,
          title: '西行法師の言葉',
          body: '平安時代の歌僧である西行法師が「この滝は四季に一度ずつ来てみなければ真の風趣は味わえない」と評したと伝えられる。厳しい冬には滝全体が真っ白に凍結する。',
          source: 'https://www.ibarakiguide.jp/spot.php?mode=detail&code=5',
          mapQuery: '袋田の滝 氷瀑',
        },
      ],
    },
    {
      id: 'kairakuen',
      name: '偕楽園の梅林',
      area: '水戸市',
      image: '/assets/scenery/ibaraki/scenes/09_kairakuen.webp',
      hotspots: [
        {
          id: 'kairakuen-founded',
          x: 0.28,
          y: 0.4,
          title: '弘道館と一対の庭',
          body: '天保13年（1842年）に、水戸藩第9代藩主の徳川斉昭が造った庭園。弘道館で文武を修業した人たちに、心身を休めて鋭気を養ってほしいと考え、弘道館と一対の施設として造られた。',
          source: 'https://www.city.mito.lg.jp/site/senbako/55226.html',
          mapQuery: '偕楽園',
        },
        {
          id: 'kairakuen-plums',
          x: 0.58,
          y: 0.62,
          title: '約100品種、3000本',
          body: '園内には約百品種・三千本の梅が植えられている。12月下旬から咲き始める冬至梅という早咲きの品種から、3月下旬頃が見頃となる江南所無のような品種まである。',
          source: 'https://ibaraki-kairakuen.jp/kairakuen/',
          mapQuery: '偕楽園 梅林',
        },
        {
          id: 'kairakuen-designation',
          x: 0.8,
          y: 0.3,
          title: '大正11年に史跡かつ名勝',
          body: '国の史跡および名勝で、指定は大正11年3月8日。指定面積は138,493平方メートルで、偕楽園の本園のほか、桜川の対岸の桜山・丸山も含まれる。',
          source: 'https://www.city.mito.lg.jp/site/education/3717.html',
          mapQuery: '偕楽園 好文亭',
        },
      ],
    },
    {
      id: 'tsukubasan',
      name: '筑波山',
      area: 'つくば市',
      image: '/assets/scenery/ibaraki/scenes/10_tsukubasan.webp',
      hotspots: [
        {
          id: 'tsukubasan-height',
          x: 0.28,
          y: 0.35,
          title: '日本百名山でいちばん低い',
          body: '筑波山は男体山と女体山の2つの峰をもつ山で、標高は877メートル。「日本百名山」に数えられる山のなかで、いちばん低い山とされる。',
          source: 'https://ttca.jp/tourisminfo/mttsukuba/',
          mapQuery: '筑波山',
        },
        {
          id: 'tsukubasan-shiho',
          x: 0.58,
          y: 0.6,
          title: '紫峰という別名',
          body: '昔から「西の富士、東の筑波」と称され、朝夕に山肌の色を変えるところから「紫峰」とも呼ばれている。',
          source: 'https://www.ibarakiguide.jp/spot.php?mode=detail&code=752',
          mapQuery: 'つくば市 筑波山',
        },
        {
          id: 'tsukubasan-plants',
          x: 0.8,
          y: 0.8,
          title: '1000種以上の植物',
          body: '山中には珍しい1,000種以上の植物が群生している。',
          source: 'https://www.ibarakiguide.jp/spot.php?mode=detail&code=752',
          mapQuery: '筑波山 女体山',
        },
      ],
    },
  ],
};
